import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RuleEvaluation } from '../types';

function isRejectActionType(actionType: string | null | undefined): boolean {
  const upper = (actionType ?? '').toUpperCase();
  return upper.includes('AUTO_REJECT') || upper.includes('REJECT') || upper.includes('HIDE');
}

function RuleRow({ rule }: { rule: RuleEvaluation }) {
  const [open, setOpen] = useState(false);
  const rejectMatch = rule.matched && isRejectActionType(rule.action_type);

  return (
    <div
      className={cn(
        'border-l-2 px-3 py-2',
        rejectMatch
          ? 'border-l-rose-500'
          : rule.matched
            ? 'border-l-green-500'
            : 'border-l-transparent opacity-60',
      )}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 text-left text-sm"
        onClick={() => setOpen((o) => !o)}
      >
        {rule.matched ? (
          <Check
            className={cn(
              'size-4 shrink-0',
              rejectMatch ? 'text-rose-500' : 'text-green-500',
            )}
            aria-label={rejectMatch ? 'Matched reject rule' : 'Matched rule'}
          />
        ) : (
          <X className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate font-medium">{rule.rule_name}</span>
        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
          {rule.condition_summary}
        </span>
        <span className="shrink-0 text-xs">{rule.action_type}</span>
      </button>

      {open && (
        <div className="ml-7 mt-2 space-y-1 text-xs text-muted-foreground">
          {rule.match_reason && (
            <p>
              <span className="font-medium text-foreground">Reason:</span> {rule.match_reason}
            </p>
          )}
          {Object.keys(rule.signal_values).length > 0 && (
            <div>
              <span className="font-medium text-foreground">Signals:</span>{' '}
              {Object.entries(rule.signal_values)
                .map(([k, v]) => `${k}=${v.toFixed(3)}`)
                .join(', ')}
            </div>
          )}
          {rule.linked_policies.length > 0 && (
            <p>
              <span className="font-medium text-foreground">Policies:</span>{' '}
              {rule.linked_policies.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function RuleTraceTable({ evaluations }: { evaluations: RuleEvaluation[] }) {
  if (evaluations.length === 0) {
    return <p className="text-sm text-muted-foreground">No rule evaluations.</p>;
  }

  return (
    <div className="divide-y rounded-md border">
      {evaluations.map((rule) => (
        <RuleRow key={rule.rule_id} rule={rule} />
      ))}
    </div>
  );
}
